import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  Signal,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SharedService } from '@ccfx/shared-lib';
import { CcfxBadgeComponent } from '@ccfx/shared-lib/badge';
import { EnvironmentBanner, Header, SideNav } from '@ccfx/shared-lib/layout';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { filter } from 'rxjs';
import { PageHelpComponent, PageHelpGlobalSearchContext } from './features/page-help/page-help';
import { PendingApprovalsService } from './features/shared/services/pending-approvals.service';
import { PermissionService } from './features/shared/services/permissions.service';
import { UserPermissionStore } from './features/shared/services/user-permission.store';

interface GlobalSearchNavigationToastState {
  searchedText?: string;
  label?: string;
  section?: string;
  description?: string;
  navigationPath?: readonly string[];
  actionHint?: string;
}

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    Header,
    TranslocoPipe,
    SideNav,
    CcfxBadgeComponent,
    EnvironmentBanner,
    PageHelpComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly titleService = inject(Title);
  private readonly sharedService = inject(SharedService);
  private readonly translocoService = inject(TranslocoService);
  private readonly permissions = inject(PermissionService);
  private readonly userPermissionStore = inject(UserPermissionStore);
  protected readonly pendingApprovals = inject(PendingApprovalsService);

  private readonly routeLabelSignals = new Map<string, Signal<string>>();
  private readonly pageHelp = viewChild(PageHelpComponent);
  private globalSearchDialogRef: MatDialogRef<unknown> | null = null;

  protected readonly currentRoute = signal<string>('');
  protected readonly isLoginRoute = computed(
    () => this.currentRoute().split(/[?#]/)[0] === '/login',
  );
  protected readonly globalSearchContext = signal<PageHelpGlobalSearchContext | null>(null);

  protected readonly title = signal('CCFX Wholesale Backend Portal');

  protected async openGlobalSearch(): Promise<void> {
    if (this.globalSearchDialogRef) {
      return;
    }

    const { GlobalSearchDialogComponent } =
      await import('./features/global-search/global-search-dialog');

    this.globalSearchDialogRef = this.dialog.open(GlobalSearchDialogComponent, {
      data: {
        title: this.translocoService.translate('globalSearch.title'),
        size: 'extra-large',
        showCloseButton: true,
      },
      panelClass: ['ccfx-modal-dialog-panel'],
      maxWidth: 'none',
      autoFocus: false,
      restoreFocus: true,
    });

    this.globalSearchDialogRef.afterClosed().subscribe(() => {
      this.globalSearchDialogRef = null;
    });
  }

  @HostListener('document:keydown.control.shift.f', ['$event'])
  protected onGlobalSearchShortcut(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    void this.openGlobalSearch();
  }

  constructor() {
    // Only start polling once the user profile has actually been loaded from the API.
    // Using `isAuthenticated` alone is insufficient: MSAL caches accounts in localStorage,
    // so `isAuthenticated` can be `true` for an expired session, causing immediate 401s
    // on the login page. Checking `profile() !== null` ensures the whoami call succeeded.
    effect(() => {
      if (this.userPermissionStore.profile() !== null) {
        this.pendingApprovals.startPolling();
      }
    });

    this.permissions.NavcomponentMap.forEach((translationKey, route) => {
      this.routeLabelSignals.set(route, this.sharedService.getTranslation(translationKey));
    });
    this.routeLabelSignals.set('login', this.sharedService.getTranslation('login.title'));
    this.routeLabelSignals.set(
      'no-access',
      this.sharedService.getTranslation('noPermission.title'),
    );

    this.currentRoute.set(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.currentRoute.set(event.urlAfterRedirects);
        this.showGlobalSearchToast();
      });

    effect(() => {
      const baseTitle = this.title();
      const routeTitle = this.getRouteLabel(this.currentRoute());
      const pageTitle = routeTitle ? `${routeTitle} - ${baseTitle}` : baseTitle;

      this.titleService.setTitle(pageTitle);
    });
  }

  private getRouteLabel(url: string): string | null {
    const path = url.split(/[?#]/)[0].replace(/^\/+/, '');
    if (!path) {
      return null;
    }

    const segment = path.split('/')[0];
    const translationSignal = this.routeLabelSignals.get(segment);

    return translationSignal ? translationSignal() : segment;
  }

  private showGlobalSearchToast(): void {
    const state = this.router.getCurrentNavigation()?.extras.state?.['globalSearchResult'] as
      | GlobalSearchNavigationToastState
      | undefined;

    if (!state?.label || !state.description) {
      // Navigating away from the searched page: clear any stale global search context
      // so the "Opened From Global Search" section doesn't leak into unrelated pages.
      this.globalSearchContext.set(null);
      return;
    }

    const context: PageHelpGlobalSearchContext = {
      searchedText: state.searchedText,
      label: state.label,
      section: state.section,
      description: state.description,
      navigationPath: state.navigationPath ?? [],
      actionHint: state.actionHint,
    };

    this.globalSearchContext.set(context);
    this.pageHelp()?.showGlobalSearchToast(context);
  }
}
